// src/utilities/categoryGroupUtils.js
//
// Client-side category "group" resolution for the Sales Call Leads filter bar.
// Leads are always tagged with a leaf ("child") category id, never a parent id,
// so selecting a bare parent category only makes sense when expanded to its
// children. Given the flat {id, name, slug, type, parent_id, inherits_from}
// list from useAllCategoryBasicData, this resolves a single selected category
// into the full set of category ids that should be queried.

/**
 * Resolves an 'avatar' (alias) category to the real child category it inherits from.
 * Avatars aren't part of the parent/child tree, so group expansion needs the real node.
 */
function resolveRealCategory(categories, category) {
  if (category?.type === 'avatar' && category.inherits_from != null) {
    const target = categories.find((c) => c.id === category.inherits_from);
    if (target) return target;
  }
  return category;
}

/**
 * Given one selected category, returns the full "group" of leaf category ids
 * to filter leads by:
 *  - a 'child' category expands to all children of its parent (siblings incl. itself)
 *  - a 'parent' category expands to all of its children
 * Returns [] if no children could be resolved (e.g. a parent with no children).
 */
export function resolveCategoryGroup(categories, category) {
  const resolved = resolveRealCategory(categories, category);
  if (!resolved) return [];

  if (resolved.type === 'parent') {
    return categories.filter((c) => c.parent_id === resolved.id).map((c) => c.id);
  }

  if (resolved.type === 'child') {
    if (resolved.parent_id == null) {
      // No parent on record — nothing to expand to, just the leaf itself.
      return [resolved.id];
    }
    return categories.filter((c) => c.parent_id === resolved.parent_id).map((c) => c.id);
  }

  return [resolved.id];
}

/**
 * Resolves a single category selection to the ids that should be sent to the
 * backend, honoring the "include sibling categories" toggle:
 *  - includeGroup=true  -> resolveCategoryGroup(...) (the whole sibling/child group)
 *  - includeGroup=false -> just the selected leaf id (a bare parent selection with
 *    the toggle off matches zero leads — surfaced as an empty-state hint by the caller).
 */
export function resolveCategorySelection(categories, category, includeGroup) {
  const resolved = resolveRealCategory(categories, category);
  if (!resolved) return [];
  if (includeGroup) return resolveCategoryGroup(categories, resolved);
  return resolved.type === 'parent' ? [] : [resolved.id];
}

/**
 * Merges a new category's resolved ids into an existing chip list (by category
 * object, not raw ids), avoiding duplicate chips for the same category id.
 */
export function addCategoryChip(existingChips, category) {
  const resolved = category;
  if (existingChips.some((c) => c.id === resolved.id)) return existingChips;
  return [...existingChips, resolved];
}

export function removeCategoryChip(existingChips, categoryId) {
  return existingChips.filter((c) => c.id !== categoryId);
}
